import { useEffect, useState } from "react"
import { CreateOpportunitiesModalProps } from "./types"
import { Modal, Typography, TextInput, Button, Stack, Grid } from "src/lib"
import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material"
import {
    commitObjectiveDraft,
    createObjectiveDraft,
    regenerateObjectiveDraft,
} from "src/services/api/opportunities"
import type { ObjectiveDraftResponse } from "src/services/api/opportunities/draftTypes"
import {
    impactedClientColumnLabel,
    orderImpactedClientColumnsFromRows,
    formatImpactedClientCell,
} from "src/services/api/opportunities/impactedClientColumns"
import { intl } from "src/internationalization/intl"
import { useOpportunitiesStore } from "src/store"
import { useSnackbarStore } from "src/store/snackbarStore"

type Step = "form" | "review"

const emptyForm = () => ({
    title: "",
    text: "",
    daysOfValid: "30" as string | number,
})

const CreateOpportunitiesModal = ({ open, onClose, ...rest }: CreateOpportunitiesModalProps) => {
    const [step, setStep] = useState<Step>("form")
    const [title, setTitle] = useState("")
    const [text, setText] = useState("")
    const [daysOfValid, setDaysOfValid] = useState<string | number>("30")
    const [draft, setDraft] = useState<ObjectiveDraftResponse | null>(null)
    const [reviewStrategyText, setReviewStrategyText] = useState("")
    const [loadingDraft, setLoadingDraft] = useState(false)
    const [loadingRegenerate, setLoadingRegenerate] = useState(false)
    const [loadingCommit, setLoadingCommit] = useState(false)
    const triggerRefresh = useOpportunitiesStore((s: any) => s.triggerRefresh)
    const showSnackbar = useSnackbarStore((s) => s.showSnackbar)

    useEffect(() => {
        if (!open) {
            const e = emptyForm()
            setStep("form")
            setTitle(e.title)
            setText(e.text)
            setDaysOfValid(e.daysOfValid)
            setDraft(null)
            setReviewStrategyText("")
            setLoadingDraft(false)
            setLoadingRegenerate(false)
            setLoadingCommit(false)
        }
    }, [open])

    const modalTitle =
        step === "form" ? intl("objectiveDraftModalTitle") : intl("objectiveDraftReviewTitle")

    const daysNum = Number(daysOfValid)
    const formValid =
        text.trim().length > 0 && Number.isFinite(daysNum) && daysNum > 0

    const applyDraftResponse = (res: ObjectiveDraftResponse) => {
        setDraft(res)
        setReviewStrategyText(res.objective?.text ?? "")
    }

    const handleGenerateDraft = async () => {
        if (!formValid) return
        setLoadingDraft(true)
        try {
            const body = {
                ...(title.trim() ? { title: title.trim() } : {}),
                text: text.trim(),
                daysOfValid: daysNum,
            }
            const res = await createObjectiveDraft(body)
            applyDraftResponse(res)
            setStep("review")
        } catch {
            // apiFetch já mostra snackbar
        } finally {
            setLoadingDraft(false)
        }
    }

    const handleRegenerate = async () => {
        if (!draft?.draft_id || !reviewStrategyText.trim()) return
        setLoadingRegenerate(true)
        try {
            const res = await regenerateObjectiveDraft({
                draft_id: draft.draft_id,
                text: reviewStrategyText.trim(),
            })
            applyDraftResponse(res)
        } catch {
            setStep("form")
            setDraft(null)
        } finally {
            setLoadingRegenerate(false)
        }
    }

    const handleCommit = async () => {
        if (!draft?.draft_id) return
        setLoadingCommit(true)
        try {
            const res = await commitObjectiveDraft({ draft_id: draft.draft_id })
            showSnackbar(res.message || intl("objectiveCommitSuccess"), "success")
            triggerRefresh()
            onClose()
        } catch {
            setStep("form")
            setDraft(null)
        } finally {
            setLoadingCommit(false)
        }
    }

    const handleBackToForm = () => {
        if (draft?.objective) {
            setTitle(draft.objective.title ?? "")
            setText(draft.objective.text ?? "")
            setDaysOfValid(draft.objective.daysOfValid ?? "30")
        }
        setDraft(null)
        setStep("form")
    }

    const previewColumns =
        draft?.impacted_preview?.length
            ? orderImpactedClientColumnsFromRows(
                  draft.impacted_preview as Record<string, unknown>[]
              )
            : []

    const expiresLabel = draft?.expires_at
        ? new Date(draft.expires_at).toLocaleString("pt-BR", {
              dateStyle: "short",
              timeStyle: "short",
          })
        : ""

    return (
        <Modal open={open} onClose={onClose} {...rest} title={modalTitle}>
            <Stack spacing={3} sx={{ maxHeight: "72vh", overflow: "auto", pr: 0.5 }}>
                {step === "form" && (
                    <>
                        <Typography variant="body2" color="secondary">
                            {intl("objectiveDraftIntro")}
                        </Typography>

                        <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {intl("objectiveReportTitleLabel")}
                            </Typography>
                            <TextInput
                                fullWidth
                                placeholder="Ex.: Oportunidade renda fixa — abril"
                                size="medium"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                        </Box>

                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                {intl("objectivePromptLabel")}
                                <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
                                    *
                                </Box>
                            </Typography>
                            <Typography variant="caption" color="secondary" sx={{ display: "block", mb: 1 }}>
                                {intl("objectivePromptHelper")}
                            </Typography>
                            <TextInput
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={14}
                                placeholder="Ex.: Priorizar clientes com caixa acima de 10% da carteira e perfil moderado ou arrojado, sugerindo títulos prefixados IPCA…"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                            />
                        </Box>

                        <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {intl("objectiveValidityLabel")}
                                <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
                                    *
                                </Box>
                            </Typography>
                            <Grid container alignItems="center" gap={1} sx={{ maxWidth: 280 }}>
                                <TextInput
                                    type="number"
                                    inputProps={{ min: 1 }}
                                    value={daysOfValid}
                                    onChange={(e) => setDaysOfValid(e.target.value)}
                                />
                                <Typography variant="body2">{intl("objectiveValiditySuffix")}</Typography>
                            </Grid>
                        </Box>

                        <Grid container justifyContent="flex-end">
                            <Button
                                variant="contained"
                                onClick={handleGenerateDraft}
                                disabled={!formValid || loadingDraft}
                            >
                                {loadingDraft ? <CircularProgress size={22} /> : intl("objectiveGenerateDraftButton")}
                            </Button>
                        </Grid>
                    </>
                )}

                {step === "review" && draft && (
                    <>
                        <Typography variant="body2" fontWeight={600}>
                            {intl("objectiveReviewHeading")}
                        </Typography>
                        {expiresLabel && (
                            <Typography variant="caption" color="secondary">
                                {intl("objectiveExpiresHint")}: {expiresLabel}
                            </Typography>
                        )}

                        {draft.can_commit === false && (
                            <Typography variant="body2" sx={{ mb: 1, color: "error.main" }}>
                                {draft.warnings?.[0] || intl("objectiveDraftCannotCommit")}
                            </Typography>
                        )}

                        <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {intl("objectiveInterpretationHeading")}
                            </Typography>
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    bgcolor: (t) => (t as any).palette?.neutral?.darker ?? "action.hover",
                                    whiteSpace: "pre-wrap",
                                }}
                            >
                                <Typography variant="body2">{draft.interpretation || "—"}</Typography>
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {intl("objectiveImpactedCount")}:{" "}
                                <Typography component="span" variant="body2" fontWeight={600}>
                                    {draft.impacted_count ?? 0}
                                </Typography>
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {intl("objectiveImpactedPreviewHeading")}
                            </Typography>
                            {!draft.impacted_preview?.length ? (
                                <Typography variant="body2" color="secondary">
                                    {intl("objectivePreviewEmpty")}
                                </Typography>
                            ) : (
                                <Table size="small" sx={{ minWidth: 520 }}>
                                    <TableHead>
                                        <TableRow>
                                            {previewColumns.map((col) => (
                                                <TableCell key={col}>
                                                    {impactedClientColumnLabel(col)}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {draft.impacted_preview.map((row, ri) => (
                                            <TableRow key={ri}>
                                                {previewColumns.map((col) => (
                                                    <TableCell key={col}>
                                                        {formatImpactedClientCell(col, row[col])}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>

                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                {intl("objectiveStrategyAdjustLabel")}
                            </Typography>
                            <TextInput
                                fullWidth
                                multiline
                                minRows={2}
                                maxRows={12}
                                value={reviewStrategyText}
                                onChange={(e) => setReviewStrategyText(e.target.value)}
                            />
                        </Box>

                        <Grid container justifyContent="space-between" gap={2} flexWrap="wrap">
                            <Button variant="outlined" color="neutral" onClick={handleBackToForm}>
                                {intl("objectiveBackToFormButton")}
                            </Button>
                            <Grid container gap={1} flexWrap="wrap" justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    onClick={handleRegenerate}
                                    disabled={
                                        !reviewStrategyText.trim() || loadingRegenerate || loadingCommit
                                    }
                                >
                                    {loadingRegenerate ? (
                                        <CircularProgress size={22} />
                                    ) : (
                                        intl("objectiveRegenerateButton")
                                    )}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleCommit}
                                    disabled={
                                        loadingCommit ||
                                        loadingRegenerate ||
                                        draft.can_commit === false
                                    }
                                >
                                    {loadingCommit ? (
                                        <CircularProgress size={22} />
                                    ) : (
                                        intl("objectiveCommitButton")
                                    )}
                                </Button>
                            </Grid>
                        </Grid>
                    </>
                )}
            </Stack>
        </Modal>
    )
}

export { CreateOpportunitiesModal }
